import LinkCard from '@/components/patterns/link-card'
import LinkCardAction from '@/components/patterns/link-card-action'
import { ScrollEdgeButton } from '@/components/patterns/scroll-edge-button'
import { CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { usePathHelper } from '@/hooks/usePathHelper'
import { CartSource } from '@/lib/enums/CartSource'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { cn, formatCurrency, getInvoiceLineItemsWithReturnedStatus } from '@/lib/utils'
import {
  Calendar,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleX,
  Mail,
  MapPinIcon,
  Phone,
  ShoppingBasket,
  ShoppingCart,
} from 'lucide-react'
import { type MouseEvent, useRef, useState } from 'react'
import MarkFulfilledForm from '../forms/Orders/MarkFulfilledForm'
import { Can } from '@/lib/casl'
import { canMoveFulfilledOrderBack } from '../forms/Orders/fulfillment-workflow'
import type {
  SalesOrderDocumentLine,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { InvoiceLineItemWithReturnedStatus } from '@/lib/utils'
import ImageThumbnail from '@/components/media/ImageThumbnail'
import { AddressFormatter } from '../ui/address-formatter'
import { Badge } from '../ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { DeliveryAddressVerificationBadge } from './DeliveryAddressVerificationBadge'
import FulfillmentTypeBadge from './FulfillmentTypeBadge'
import OrderSourceBadge from './OrderSourceBadge'

import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { memo } from 'react'
import AssignDesignerForm from '../forms/Orders/AssignDesignerForm'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import PrintOrder from './PrintOrder'
import {
  canUpdateSalesOrderFulfillment,
  getSalesOrderFulfillmentDuePresentation,
  getSalesOrderRemainingTotal,
  isCancelledSalesOrder,
  shouldShowSalesOrderBalanceDue,
} from './sales-order-presentation'
import {
  getProductLineItemGroups,
  getProductLineItemSalesOrderItemId,
  type ProductLineItemGroupSource,
} from './order-product-line-groups'
import { FulfillmentSurface } from './FulfillmentSurface'
import { Typography } from '@/components/ui/typography'

/** A document product line on the order row, with how many units are left after returns. */
type OrderCardLineItem = InvoiceLineItemWithReturnedStatus<SalesOrderDocumentLine>

function getOrderCardLineItemKey(
  item: ProductLineItemGroupSource,
  fallbackIndex: number
): number | string {
  return item.quantityId ?? item.id ?? getProductLineItemSalesOrderItemId(item) ?? fallbackIndex
}

function handleOrderCardFooterClick(event: MouseEvent<HTMLDivElement>) {
  event.stopPropagation()

  if (event.target instanceof Element && event.target.closest('button[type="button"]')) {
    event.preventDefault()
  }
}

type OrderCardProductLineItemProps = {
  item: OrderCardLineItem
  order: Pick<SalesOrderListItem, 'fulfillment_type'>
  relationship?: 'root' | 'child'
}

function OrderCardProductLineItem({
  item,
  order,
  relationship = 'root',
}: OrderCardProductLineItemProps) {
  // The line's snapshot carries the item's name, picture, and badge. (The index never loads the live
  // variant: the API builds a missing snapshot from it instead.)
  const salesOrderItem = item.sales_order_item
  const display = getLineItemDisplay(
    salesOrderItem ? { variant_details: salesOrderItem.variant_details } : salesOrderItem
  )
  const isChild = relationship === 'child'

  return (
    <div
      data-relationship={relationship}
      className={cn(
        'flex items-start',
        isChild ? 'space-x-2.5' : 'space-x-3',
        item.remainingQuantity === 0 && 'pointer-events-none'
      )}
    >
      {display.image && (
        <div
          className={cn(
            'flex aspect-square items-center justify-center rounded-md bg-muted',
            isChild ? 'min-h-14 min-w-14 max-w-14 max-h-14' : 'min-h-20 min-w-20 max-w-20 max-h-20'
          )}
        >
          <ImageThumbnail
            className="aspect-square h-full w-full object-cover"
            media={display.image}
          />
        </div>
      )}
      <div className="flex-1">
        <div className="flex flex-col">
          <div className="flex justify-between">
            <Typography variant="label" className="whitespace-pre-line">
              {display.primaryName}
            </Typography>
          </div>
          <div className="mb-1 flex flex-wrap items-center gap-1">
            {display.badgeLabel && (
              <Badge
                variant={
                  order.fulfillment_type === FulfillmentType.Delivery.Value ? 'brand' : 'secondary'
                }
                size="xs"
              >
                {display.badgeLabel}
              </Badge>
            )}
            {item.remainingQuantity === 0 && (
              <Badge variant="destructive" size="xs" className="w-fit">
                Returned
              </Badge>
            )}
          </div>
          {display.description && (
            <Typography variant="caption" className="mb-1 line-clamp-2 whitespace-pre-line">
              {display.description}
            </Typography>
          )}
          {item.remainingQuantity !== 0 && (
            <Typography variant="caption" className="mb-1">
              Quantity: {item.remainingQuantity}
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

type OrderCardProps = {
  /** An order row from the sales order index. */
  order: SalesOrderListItem
}

const OrderCard = memo(({ order }: OrderCardProps) => {
  const [isAtTop, setIsAtTop] = useState(true)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const path = usePathHelper()
  const { formatPhone } = usePhoneFormatter()
  const purchaser = order.purchaser
  const purchaserName = purchaser?.name?.trim() || '(None provided)'
  const purchaserPhone = purchaser?.phone ? formatPhone(purchaser.phone) : '(None provided)'
  const purchaserEmail = purchaser?.email?.trim() || '(None provided)'
  const isPosOrder = order.source === CartSource.POS.Value
  const remainingTotal = getSalesOrderRemainingTotal(order)
  const isCancelled = isCancelledSalesOrder(order)
  // Moving a delivered or picked-up order back needs no payment, only moving it forward does.
  const canMarkFulfilled = canUpdateSalesOrderFulfillment(order) || canMoveFulfilledOrderBack(order)
  const fulfillmentDueLabel = getSalesOrderFulfillmentDuePresentation(order)?.label

  const listRef = useRef<HTMLUListElement>(null)

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current
      setIsAtTop(scrollTop === 0)
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 1) // -1 for rounding errors
    }
  }

  const invoiceLineItems = getInvoiceLineItemsWithReturnedStatus(order)
  const invoiceProductLineItems = invoiceLineItems.filter(
    (item) => item.type === TransactionLineType.Product.Value
  )
  const invoiceProductLineItemGroups = getProductLineItemGroups(invoiceProductLineItems)

  return (
    <LinkCard
      key={order.id}
      {...path.salesOrders.show(order.id).using({ orgId: order.organization_id ?? undefined }).link}
      className="flex h-full flex-col gap-4 pt-4 pb-2"
    >
      <CardHeader size="xs">
        <FulfillmentSurface
          fulfillmentType={order.fulfillment_type}
          className="-mt-2 flex min-h-[110px] flex-col gap-2 rounded-md border p-2"
        >
          <Typography
            as="div"
            variant="labelStrong"
            tone="inherit"
            className="flex justify-between"
          >
            <div className="flex items-center gap-2">
              <span>#{order.id}</span>
              {isPosOrder && <OrderSourceBadge source={order.source} size="xs" />}
            </div>
            <div className="flex items-center gap-2">
              {order.fulfillment_type === FulfillmentType.Delivery.Value && (
                <DeliveryAddressVerificationBadge deliveryAddress={order.delivery_address} />
              )}
              <FulfillmentTypeBadge fulfillmentType={order.fulfillment_type} />
            </div>
          </Typography>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <ShoppingCart className="w-4 h-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Purchaser</p>
                </TooltipContent>
              </Tooltip>
              <Typography variant="labelStrong" weight="bold" tone="inherit" align="left">
                {purchaserName}
              </Typography>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Phone className="w-4 h-4" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{purchaserPhone}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Mail className="w-4 h-4" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{purchaserEmail}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="flex items-start justify-start gap-1">
            <div className="flex items-center gap-2">
              {order.fulfillment_type === 'delivery' && order.delivery_address ? (
                <LinkCardAction>
                  <AddressFormatter
                    address={order.delivery_address}
                    name={order.recipient_name}
                    variant="short"
                    icon={<MapPinIcon className="w-4 h-4" />}
                    isLink={false}
                  />
                </LinkCardAction>
              ) : (
                <LinkCardAction>
                  <AddressFormatter
                    address={order.location?.address ?? undefined}
                    name={
                      order.location?.address?.name
                        ? `${order.location?.address?.name} Location`
                        : `Location #${order.location?.id} `
                    }
                    variant="short"
                    icon={<ShoppingBasket className="w-4 h-4" />}
                    isLink={false}
                  />
                </LinkCardAction>
              )}
            </div>
          </div>
          {fulfillmentDueLabel ? (
            <div className="flex items-start justify-start">
              <LinkCardAction>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <Typography variant="label" tone="inherit" align="left">
                    {fulfillmentDueLabel}
                  </Typography>
                </div>
              </LinkCardAction>
            </div>
          ) : null}
        </FulfillmentSurface>
      </CardHeader>
      <CardContent size="xs" className="flex flex-1 flex-col">
        {invoiceProductLineItems.length > 2 && !isAtTop && (
          <div className="flex w-full justify-center">
            <ScrollEdgeButton
              edge="top"
              aria-label="Scroll order items up"
              variant={
                order.fulfillment_type === FulfillmentType.Delivery.Value ? 'default' : 'secondary'
              }
              className="-mb-5 z-10"
              onClick={() => {
                listRef.current?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }}
            >
              <ChevronUpIcon className="size-4" />
            </ScrollEdgeButton>
          </div>
        )}
        <ul
          className="space-y-3 max-h-[175px] overflow-y-auto no-scrollbar flex-1"
          ref={listRef}
          onScroll={handleScroll}
        >
          {invoiceProductLineItemGroups.map((group) => (
            <li key={group.key} className="space-y-2">
              <div className="space-y-2">
                {group.rootItems.map((item, rootIndex) => (
                  <OrderCardProductLineItem
                    key={getOrderCardLineItemKey(item, rootIndex)}
                    item={item}
                    order={order}
                  />
                ))}
              </div>

              {group.childItems.length > 0 ? (
                <ul className="ml-5 space-y-2 border-l border-border/70 pl-3" role="list">
                  {group.childItems.map((childItem, childIndex) => (
                    <li
                      key={getOrderCardLineItemKey(childItem, childIndex)}
                      data-testid="order-card-child-line-item"
                      className="relative"
                    >
                      <span
                        aria-hidden="true"
                        className="absolute -left-3 top-6 h-px w-2.5 bg-border/70"
                      />
                      <OrderCardProductLineItem
                        item={childItem}
                        order={order}
                        relationship="child"
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
        {invoiceProductLineItems.length > 2 && !isAtBottom && (
          <div className="flex w-full justify-center">
            <ScrollEdgeButton
              edge="bottom"
              aria-label="Scroll order items down"
              variant={
                order.fulfillment_type === FulfillmentType.Delivery.Value ? 'default' : 'secondary'
              }
              className="-mt-3 z-10"
              onClick={() => {
                const list = listRef.current
                list?.scrollTo({
                  top: list.scrollHeight,
                  behavior: 'smooth',
                })
              }}
            >
              <ChevronDownIcon className="size-4" />
            </ScrollEdgeButton>
          </div>
        )}
      </CardContent>
      <CardFooter size="xs" className="flex w-full flex-col" onClick={handleOrderCardFooterClick}>
        {isCancelled ? (
          <div className="flex justify-between mt-2 w-full">
            <Badge variant="destructive" size="sm">
              <CircleX className="size-3" />
              Cancelled
            </Badge>
          </div>
        ) : shouldShowSalesOrderBalanceDue(order) ? (
          <div className="flex justify-between mt-2 w-full">
            <Badge variant="warning" size="sm">
              Balance due {formatCurrency(remainingTotal)}
            </Badge>
          </div>
        ) : null}
        <div className="flex justify-between mt-2 w-full">
          <div className="flex gap-2 w-full">
            <IfGlobalOrg show={false}>
              {!isCancelled ? (
                <div className="flex-1 min-w-0">
                  <AssignDesignerForm order={order} />
                </div>
              ) : (
                <div className="flex-1 min-w-0" />
              )}
              <div className="flex-shrink-0">
                <div className="flex items-center justify-end">
                  <PrintOrder orderId={order.id} size="sm" variant="outline" bubbleTriggerClick />
                </div>
              </div>
            </IfGlobalOrg>
          </div>
        </div>
        <div className="flex justify-between mt-2 w-full">
          <div className="flex-1">
            {canMarkFulfilled ? (
              <Can do="edit" on="sales-orders" field="fulfillment">
                <MarkFulfilledForm order={order} />
              </Can>
            ) : null}
          </div>
        </div>
      </CardFooter>
    </LinkCard>
  )
})

export default OrderCard
